/* WenStorm analytics — the POSTHOG provider and the bootstrap.
 *
 * This file knows PostHog exists. It does NOT know it is running on Shopify -- that moved to
 * wenstorm-analytics-shopify.js, so the two things that will be replaced at different times are no
 * longer replaced together. An earlier comment here claimed this was "the only file that knows
 * PostHog exists and the only one that knows it is on Shopify"; that was never quite true, since
 * the Liquid bootstrap plainly knows about Shopify too. The boundary that actually matters is:
 *
 *     platform-neutral   wenstorm-analytics.js
 *     vendor             wenstorm-analytics-posthog.js   <- this file
 *     frontend           wenstorm-analytics-shopify.js + wenstorm-analytics.liquid
 */
(function (global, doc) {
  'use strict';

  /* ---- provider -------------------------------------------------------------------------------
   * A thin shim, not a wrapper around PostHog's whole surface. If this grows methods, the event
   * layer has started leaking provider concepts and should be pushed back. */
  /* RESOLVES window.posthog AT CALL TIME. It must not hold a reference.
   *
   * The loader stub that runs inline is a DIFFERENT OBJECT from the library that array.js installs.
   * Holding the stub meant every event fired after load went into the stub's queue -- which nobody
   * flushes once the real library has taken over -- and vanished silently. Verified directly:
   * provider._ph === window.posthog was false, and the held object still had the stub's _i queue.
   *
   * Only page_viewed survived, because it fires during bootstrap while the stub's queue is still
   * going to be replayed. Every click, fired later, was lost. That is why the controlled run showed
   * 7 page_viewed events and zero forum_thread_clicked / product_clicked / action_clicked. */
  function PostHogProvider() {}
  /* Applied to EVERY event PostHog sends, autocapture included -- which is the point: those rows
   * never pass through the event layer, so this is the only way they learn which page visit they
   * belong to. register() persists, so each page overwrites the previous value; a page where our
   * script failed but PostHog still loaded would leave raw rows carrying a stale id, which is why
   * the repository must verify the id against that tab's own page_viewed boundaries rather than
   * trusting it blindly. Resolved at call time, like capture(), never held from boot. */
  PostHogProvider.prototype.setGlobalProperties = function (props) {
    var ph = global.posthog;
    if (!ph || typeof ph.register !== 'function') return false;
    ph.register(props);
    return true;
  };

  PostHogProvider.prototype.capture = function (name, props) {
    var ph = global.posthog;
    if (!ph || typeof ph.capture !== 'function') return false;
    ph.capture(name, props);
    return true;
  };

  /* ---- bootstrap ------------------------------------------------------------------------------ */
  global.wenstormAnalyticsBootstrap = function (opts) {
    opts = opts || {};
    var analytics = global.wenstormAnalytics;
    if (!analytics) return;

    analytics.setContext(opts.context);

    /* THEME EDITOR: never collect. Shopify re-renders sections as you drag them, so an editing
     * session would otherwise be recorded as a real visitor viewing the same product fifteen times
     * with eighty-seven clicks. Shopify documents design_mode for exactly this exclusion. Checked
     * BOTH signals: Liquid's request.design_mode is passed in by the bootstrap, and Shopify.designMode
     * is read here, because the Liquid value is baked at render time and a section re-rendered inside
     * the editor would carry a stale one. */
    if (opts.designMode || (global.Shopify && global.Shopify.designMode)) return;

    /* Resolve the project: an explicit theme setting first, then the dev fallback which Liquid
     * only emits on a NON-PUBLISHED theme. No key means no collection -- so pushing this file to
     * the live theme without configuring a setting collects nothing rather than sending live
     * traffic into the development project. Failing closed is the point. */
    var key = opts.apiKey || opts.fallbackDevKey || '';
    if (!key || !opts.apiHost) return;
    opts = { context: opts.context, designMode: opts.designMode, apiKey: key, apiHost: opts.apiHost };

    // PostHog's official loader stub runs INLINE in the Liquid snippet -- it has to, it is what
    // defines window.posthog and queues calls made before the real library downloads. By the time
    // this runs, window.posthog is that queueing stub; init() is queued and replayed on load.
    if (!global.posthog) return;

    global.posthog.init(opts.apiKey, {
      api_host: opts.apiHost,
      // Matches the project's own snippet defaults so this init and PostHog's dashboard agree.
      defaults: '2026-05-30',
      // Anonymous visitors get no person profile. Sessions and journeys still work -- which is all
      // this spike needs -- and it avoids creating a person record for every passer-by.
      person_profiles: 'identified_only',
      // Autocapture stays ON: it is what makes clicks recoverable without predicting every element
      // in advance, and the semantic layer above turns the raw capture into readable actions.
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
      // Masked by default. This is a spike on a dev-test theme, and there is no reason for input
      // text to be in the recording to prove a navigation path.
      session_recording: { maskAllInputs: true },
      // NO identify() CALL, EVER. A wenstorm_uid is attached as an event property instead, by
      // wenstorm-account-identity.liquid. identify() performs an irreversible person merge in
      // PostHog, and on a shared browser it would merge two real people permanently.
      //
      // THE LAST-LINE GUARD ON THAT PROPERTY.
      //
      // The identity snippet clears a previous account's uid by queueing unregister() on the
      // loader stub before init() runs. MEASURED with the real library, three runs per variant:
      // whether the FIRST event captured straight after init() sees that clear depends on
      // library-internal ordering. With capture_pageview:true it did; with capture_pageview:false
      // the first page_viewed went out carrying the PREVIOUS account's uid, while an event 900ms
      // later was clean -- a narrow race at init, not a dead unregister.
      //
      // Today's configuration happens to fall the safe way. That is not something to ship a
      // customer-identity claim on: it is a property of posthog-js's internals, and a version bump
      // could flip it silently, in the direction of filing one person's browsing under another
      // person's name.
      //
      // So the property is checked at the last possible moment instead. Every event -- ours,
      // autocapture, $pageview, $pageleave -- passes through here, and a wenstorm_uid that does not
      // match what THIS PAGE knows its account to be is removed rather than sent. The page sets
      // that value synchronously in <head> and updates it when the signed App Proxy request
      // resolves; absent means "no account known", and absent is always the safe answer.
      before_send: function (event) {
        try {
          if (event && event.properties && event.properties.wenstorm_uid != null) {
            var expected = global.__wenstormAccountUid;
            if (expected === undefined) expected = null;
            if (event.properties.wenstorm_uid !== expected) {
              delete event.properties.wenstorm_uid;
            }
          }
        } catch (e) {
          /* FAIL CLOSED FOR IDENTITY, OPEN FOR THE EVENT.
             If the check itself cannot run, we do not know whether the attribution is right --
             and "we do not know" must resolve to no attribution, never to the attribution that
             happened to be attached. The event is still worth keeping; the claim about who did it
             is not. Removing it is itself wrapped, because a guard whose failure path can throw is
             not a guard. */
          try { if (event && event.properties) delete event.properties.wenstorm_uid; } catch (e2) {}
        }
        return event;
      },
    });

    analytics.use(new PostHogProvider()).enable();
    // The frontend adapter is injected, not imported: this file must not depend on Shopify.
    if (typeof global.wenstormInstallShopifyAdapter === 'function') {
      global.wenstormInstallShopifyAdapter(analytics);
    }
    /* ORDER MATTERS, and it was wrong before: the page instance is opened FIRST, so page_viewed and
     * every semantic click that follows carry the same page_instance_id as the engagement record
     * for this page. Previously the id was minted inside startEngagement(), one line too late, and
     * page_viewed went out without it -- leaving the repository to re-join a page view to its own
     * engagement by URL and timestamp proximity, a heuristic standing in for an identity we had. */
    analytics.startPageInstance();
    analytics.pageViewed();
    // Begins the per-page visible/active measurement. Neutral-layer concern; started here because
    // the bootstrap is what knows the page is now live and capturing.
    analytics.startEngagement();
  };
})(window, document);
